import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Phone, MapPin, Download, CheckCircle2, Star, Trophy } from "lucide-react";
import { TornEdge } from "../../components/sport/TornEdge";
import { api } from "../../lib/api";
import {
  HISTOIRE_CLUB, SOIR_DE_MATCH, ACCES_HALLE, BENEVOLE, PRESSE, ORGANIGRAMME,
  CENTRE_FORMATION, ACADEMIE, SECTION_SPORTIVE, FEMININES_PRO,
  EQUIPE_RESERVE, JOUEURS_FORMATION, LES_EQUIPES, REJOINDRE, PRESSE_MEDIAS, BENEVOLE_FULL,
} from "./sportContent";
import { FEMALE_SILHOUETTE, MALE_SILHOUETTE } from "../../lib/silhouettes";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DARK = "#160732";
const BG_DEEP = "#280F4F";

const PAGES = {
  histoire: { title: "L'histoire du club", eyebrow: "Le Club · Depuis 1970", data: HISTOIRE_CLUB, kind: "history" },
  "soir-de-match": { title: "Soir de match", eyebrow: "Infos pratiques", data: SOIR_DE_MATCH, kind: "blocks" },
  salle: { title: "Halle Polyvalente", eyebrow: "Accès & Stade", data: ACCES_HALLE, kind: "blocks-with-address" },
  benevole: { title: "Devenez bénévole", eyebrow: "Rejoindre le club", data: BENEVOLE, kind: "missions" },
  presse: { title: "Presse & Médias", eyebrow: "Espace journalistes", data: PRESSE, kind: "press" },
  organigramme: { title: "Organigramme", eyebrow: "Le Club · L'équipe", data: ORGANIGRAMME, kind: "org" },
  "centre-formation": { title: "Centre de formation", eyebrow: "Formation des talents", data: CENTRE_FORMATION, kind: "highlights" },
  academie: { title: "Académie « Istres Terre de Handball »", eyebrow: "Pour les jeunes", data: ACADEMIE, kind: "rich" },
  "section-sportive": { title: "Section Sportive Lycée Arthur Rimbaud", eyebrow: "Formation scolaire & sportive", data: SECTION_SPORTIVE, kind: "rich" },
  staff: { title: "Le staff", eyebrow: "Encadrement technique", data: ORGANIGRAMME, kind: "org" },
  valeurs: { title: "Nos valeurs", eyebrow: "Ce qui nous fait vibrer",
    data: { hero_image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F0J5A5894-scaled-e1775742256706.jpg",
      intro: "Passion, famille, excellence, engagement : 4 valeurs qui guident chaque action du club, du babyhand à l'équipe pro.",
      sections: [
        { title: "Passion", paragraphs: ["Le handball est dans l'ADN de notre territoire. Chaque match, chaque entraînement est animé par cette même flamme — celle qui pousse nos joueurs, nos staffs et nos supporters à donner le meilleur d'eux-mêmes."] },
        { title: "Famille", paragraphs: ["De l'école de hand à l'équipe pro, le Istres Provence Handball est avant tout une grande famille. Bénévoles, partenaires, supporters : tous engagés pour faire grandir le club ensemble."] },
        { title: "Excellence", paragraphs: ["Notre exigence quotidienne nourrit notre place en Liqui Moly StarLigue et notre formation reconnue. Travail, rigueur et progression sont les piliers de cette culture du résultat."] },
        { title: "Engagement", paragraphs: ["Engagement sportif, engagement territorial. Le club rayonne sur tout l'Ouest Provence à travers son académie, sa section sportive et ses actions auprès des jeunes."] },
      ] },
    kind: "rich" },
  feminines: { title: "Équipe première féminine", eyebrow: "Le Club · Féminines", data: FEMININES_PRO, kind: "roster", competition: { label: "Championnat National 2 Féminin", standings_url: "https://www.ff-handball.fr/competitions/saison-2024-2025-20/national-feminin-21/o-2024-2025-poule-1-1395/", calendar_url: "https://www.ff-handball.fr/competitions/saison-2024-2025-20/national-feminin-21/o-2024-2025-poule-1-1395/" }, gender: "female" },
  reserve: { title: "Équipe Réserve & Centre de formation", eyebrow: "Le Club · Pré-Pro", data: EQUIPE_RESERVE, kind: "roster", competition: { label: "Championnat National 2 Masculin", standings_url: "https://www.ff-handball.fr/competitions/saison-2024-2025-20/national-masculin-7/o-2024-2025-poule-2-1373/", calendar_url: "https://www.ff-handball.fr/competitions/saison-2024-2025-20/national-masculin-7/o-2024-2025-poule-2-1373/" }, gender: "male" },
  "joueurs-formation": { title: "Joueurs issus de la formation", eyebrow: "Centre de formation · Fierté du club", data: JOUEURS_FORMATION, kind: "alumni" },
  "les-equipes": { title: "Toutes nos équipes", eyebrow: "Le Club · Du Baby-Hand au Pro", data: LES_EQUIPES, kind: "teams-grid" },
  rejoindre: { title: "Rejoindre une équipe", eyebrow: "Inscriptions saison 25-26", data: REJOINDRE, kind: "join" },
  "presse-medias": { title: "Presse & Médias", eyebrow: "Espace journalistes · Kit médias", data: PRESSE_MEDIAS, kind: "press-kit" },
  amateur: { title: "Secteur Amateur", eyebrow: "Le Club · Bénévoles & Loisir", data: {
    hero_image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F0J5A2181-scaled-e1777474281723.jpg",
    intro: "Du babyhand à l'équipe seniors, le secteur amateur fait vivre le club au quotidien et nourrit chaque génération de futurs handballeurs.",
    sections: [
      { title: "De 4 à 77 ans", paragraphs: ["Le secteur amateur structure plus de 20 équipes : babyhand, mini-hand, U11, U13, U15, U17, U18, seniors, loisirs… L'équipe pédagogique forme des éducateurs diplômés tout au long de la saison."] },
      { title: "Encadrement et valeurs", bullets: [
        { title: "Compétition", text: "Engagements régionaux et nationaux sur toutes les catégories d'âge." },
        { title: "Loisir & Handfit", text: "Pour pratiquer le handball à tout âge, sans pression compétitive." },
        { title: "Bénévoles & arbitres", text: "Une école d'arbitrage et un pôle bénévoles toujours en recherche de bonnes volontés." },
      ] },
    ],
    cta: { label: "Rejoindre une équipe", to: "/sport/rejoindre" },
    contact_email: "amateur@istreshandball.com",
  }, kind: "rich" },
  contact: { title: "Contact", eyebrow: "Nous joindre", data: {
    hero_image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F0J5A3051-scaled-e1775208468422.jpg",
    intro: "Une question ? L'équipe du Istres Provence Handball vous répond rapidement — choisissez le bon interlocuteur ci-dessous.",
    blocks: [
      { icon: "📧", title: "Contact général", text: "contact@istreshandball.com" },
      { icon: "🎟️", title: "Billetterie & Communication", text: "Anthony Defin-Gravinese" },
      { icon: "🤝", title: "Partenariats", text: "business@istreshandball.com — Lilian Di Salvo & Emma Counil" },
      { icon: "🏟️", title: "Halle Polyvalente", text: "Boulevard Maître Gabriel Fauré, 13800 Istres" },
    ],
  }, kind: "blocks" },
};

// Generate initials from full name
function initials(name) {
  if (!name) return "?";
  return name.split(" ").filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase() || "").join("");
}

// Stable color from name
function avatarBg(name) {
  const palette = ["#4F1B7C", "#EC1F7C", "#7B2CBF", "#C2185B", "#5E2A84", "#AD1457", "#3D1457"];
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

export default function SportClubInnerPage() {
  const { slug } = useParams();
  // Allow /sport/contact (no slug) to load the "contact" page
  const key = slug || (typeof window !== "undefined" && window.location.pathname.endsWith("/sport/contact") ? "contact" : slug);
  const page = PAGES[key];

  // CMS override : si un admin a édité cette page via /admin/sport-pages, on
  // affiche sa version éditée (body_html) à la place du contenu structuré.
  const [override, setOverride] = useState(null); // { title, cover_image_url, body_html } | null
  const [overrideLoaded, setOverrideLoaded] = useState(false);
  useEffect(() => {
    if (!key) { setOverrideLoaded(true); return; }
    let mounted = true;
    api.get(`/sport/pages/${key}`)
      .then((r) => {
        if (!mounted) return;
        const p = r?.data?.page;
        if (p && (p.body_html || p.content_html)) {
          setOverride({
            title: p.title || page?.title,
            eyebrow: p.eyebrow || page?.eyebrow,
            cover_image_url: p.cover_image_url || p.hero_image || page?.data?.hero_image,
            body_html: p.body_html || p.content_html,
          });
        }
      })
      .catch(() => {})
      .finally(() => mounted && setOverrideLoaded(true));
    return () => { mounted = false; };
  }, [key, page]);

  // Affiche la version CMS éditée si présente (override prioritaire).
  if (overrideLoaded && override) {
    return (
      <div className="bg-white" data-testid="club-inner-cms-override">
        <section className="relative pt-32 pb-12 sm:pt-40 sm:pb-20 overflow-hidden">
          <div className="absolute inset-0 z-0">
            {override.cover_image_url && (
              <img src={override.cover_image_url} alt="" className="absolute inset-0 w-full h-full object-cover"/>
            )}
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${BG_DARK}cc 0%, ${PRIMARY}aa 60%, ${BG_DARK}ee 100%)` }}/>
          </div>
          <div className="relative max-w-5xl mx-auto px-4 lg:px-8 text-center text-white">
            {override.eyebrow && (
              <div className="text-pink-300 font-black tracking-[0.4em] text-xs uppercase mb-3">{override.eyebrow}</div>
            )}
            <h1 className="font-black uppercase italic" style={{ fontSize: "clamp(2rem, 5vw, 4rem)", lineHeight: 0.95, color: "white" }}>
              {override.title}
            </h1>
          </div>
        </section>
        <article
          className="prose prose-slate prose-img:rounded-xl prose-headings:font-black prose-headings:uppercase prose-headings:italic max-w-4xl mx-auto px-4 lg:px-8 py-12"
          dangerouslySetInnerHTML={{ __html: override.body_html }}
          data-testid="club-inner-cms-body"
        />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center text-slate-500">
          <div className="text-2xl font-black uppercase italic" style={{ color: PRIMARY }}>Page introuvable</div>
          <Link to="/sport" className="mt-4 inline-flex items-center gap-2 text-pink-600 font-bold uppercase text-xs tracking-widest">Retour à l'accueil <ArrowRight size={13}/></Link>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative pt-32 pb-12 sm:pt-40 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={page.data.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover"/>
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${BG_DARK}cc 0%, ${PRIMARY}aa 60%, ${BG_DARK}ee 100%)` }}/>
        </div>
        <div className="relative max-w-5xl mx-auto px-4 lg:px-8 text-center text-white">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="text-pink-300 font-black tracking-[0.4em] text-xs uppercase mb-3">{page.eyebrow}</motion.div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-black uppercase italic" style={{ fontSize: "clamp(2rem, 5vw, 4rem)", lineHeight: 0.95, color: "white" }}>
            {page.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="mt-5 text-white/90 max-w-3xl mx-auto leading-relaxed text-lg">
            {page.data.intro}
          </motion.p>
        </div>
        <TornEdge position="bottom" color="#ffffff" variant="shred" height={56}/>
      </section>

      {/* CONTENT */}
      <section className="relative py-16">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          {page.kind === "history" && (
            <div className="space-y-5 text-slate-700 leading-relaxed">
              {page.data.paragraphs.map((p, i) => (
                <motion.p key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="text-[16px]">{p}</motion.p>
              ))}
              <div className="mt-8 text-center">
                <Link to="/sport/club/histoire" className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-black uppercase text-xs tracking-widest text-white shadow-lg hover:scale-105 transition"
                  style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                  Voir la timeline animée <ArrowRight size={14}/>
                </Link>
              </div>
            </div>
          )}

          {(page.kind === "blocks" || page.kind === "blocks-with-address") && (
            <>
              {page.kind === "blocks-with-address" && page.data.address && (
                <div className="mb-8 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-pink-50 text-pink-700 font-bold">
                  <MapPin size={16}/> {page.data.address}
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-5">
                {page.data.blocks.map((b, i) => (
                  <motion.div key={b.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                    className="rounded-2xl bg-white shadow-lg border border-slate-100 p-6 hover:shadow-xl transition">
                    <div className="text-3xl mb-2" aria-hidden>{b.icon}</div>
                    <h3 className="font-black uppercase text-base mb-1" style={{ color: PRIMARY }}>{b.title}</h3>
                    <p className="text-slate-600 text-sm">{b.text}</p>
                  </motion.div>
                ))}
              </div>
            </>
          )}

          {page.kind === "missions" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {page.data.missions.map((m, i) => (
                <motion.div key={m.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                  className="rounded-2xl bg-white shadow-lg border border-slate-100 p-6 hover:shadow-xl hover:border-pink-300 transition">
                  <h3 className="font-black uppercase text-base mb-2" style={{ color: PRIMARY }}>{m.title}</h3>
                  <p className="text-slate-600 text-sm">{m.text}</p>
                </motion.div>
              ))}
              <div className="sm:col-span-2 lg:col-span-3 text-center mt-6">
                <a href="mailto:benevoles@istreshandball.com" className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-black uppercase text-xs tracking-widest text-white shadow-lg hover:scale-105 transition"
                  style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                  <Mail size={14}/> Devenir bénévole <ArrowRight size={14}/>
                </a>
              </div>
            </div>
          )}

          {page.kind === "press" && (
            <div className="space-y-8">
              <div className="rounded-2xl p-6 bg-pink-50 border border-pink-100">
                <div className="text-pink-600 font-black tracking-widest text-[10px] uppercase mb-2">Contact presse</div>
                <div className="font-black text-lg" style={{ color: PRIMARY }}>{page.data.contact.name}</div>
                <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-700">
                  <a href={`mailto:${page.data.contact.email}`} className="inline-flex items-center gap-1.5 hover:text-pink-600"><Mail size={14}/> {page.data.contact.email}</a>
                  <a href={`tel:${page.data.contact.phone}`} className="inline-flex items-center gap-1.5 hover:text-pink-600"><Phone size={14}/> {page.data.contact.phone}</a>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {page.data.resources.map((r, i) => (
                  <Link key={r.title} to={r.url}
                    className="rounded-2xl bg-white shadow-lg border border-slate-100 p-5 hover:shadow-xl hover:border-pink-300 transition group">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-full text-white mb-3" style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                      <Download size={16}/>
                    </div>
                    <h3 className="font-black uppercase text-sm" style={{ color: PRIMARY }}>{r.title}</h3>
                    <p className="text-slate-500 text-xs mt-1">{r.desc}</p>
                    <div className="mt-3 inline-flex items-center gap-1 text-pink-600 text-[11px] font-black uppercase tracking-widest group-hover:gap-2 transition">Accéder <ArrowRight size={11}/></div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {page.kind === "org" && (
            <div data-testid="org-content" className="space-y-10">
              {(page.data.blocks || []).map((b, i) => (
                <motion.div key={b.title}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}>
                  <div className="flex items-center gap-3 mb-5">
                    {b.tone === "premium" && <Star size={16} className="text-pink-500"/>}
                    <h3 className="font-black uppercase italic" style={{ color: PRIMARY, fontSize: "clamp(1.25rem, 2.5vw, 1.75rem)" }}>
                      {b.title}
                    </h3>
                    <div className="flex-1 h-px bg-gradient-to-r from-pink-500/40 to-transparent"/>
                  </div>
                  <div className={`grid gap-4 ${b.tone === "premium"
                    ? "grid-cols-1 sm:grid-cols-1 max-w-md"
                    : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
                    {(b.members || []).map((m) => (
                      <div key={m.name}
                        data-testid={`org-member-${m.name.replace(/\s+/g, '-').toLowerCase()}`}
                        className="group relative rounded-2xl bg-white shadow-md hover:shadow-2xl border border-slate-100 hover:border-pink-200 transition overflow-hidden">
                        {/* halftone deco on hover */}
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition pointer-events-none" style={{
                          backgroundImage: `radial-gradient(circle at top right, ${SECONDARY}10, transparent 70%)`,
                        }}/>
                        <div className="relative p-5 flex items-start gap-4">
                          {/* Avatar : real photo if provided, otherwise neutral silhouette (matches reference site) */}
                          <div className="relative shrink-0">
                            <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center shadow-lg ring-4 ring-white"
                              style={{ background: m.photo ? `linear-gradient(135deg, ${avatarBg(m.name)}, ${SECONDARY})` : "#e8eaf3" }}>
                              <img
                                src={m.photo || "https://istreshandball.com/wp-content/uploads/2024/01/silhouette.png"}
                                alt={m.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                onError={(e) => {
                                  e.currentTarget.src = "https://istreshandball.com/wp-content/uploads/2024/01/silhouette.png";
                                }}
                              />
                            </div>
                            {b.tone === "premium" && (
                              <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center shadow"
                                title="Présidence">
                                <Star size={11} className="text-white" fill="white"/>
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-black uppercase text-sm leading-snug" style={{ color: PRIMARY }}>{m.name}</div>
                            <div className="text-slate-500 text-xs mt-0.5 leading-snug">{m.role}</div>
                            {(m.email || m.phone) && (
                              <div className="mt-3 flex flex-wrap items-center gap-2">
                                {m.phone && (
                                  <a href={`tel:${m.phone.replace(/[\s.]/g, '')}`}
                                    data-testid={`call-${m.name.replace(/\s+/g, '-').toLowerCase()}`}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-white shadow hover:scale-105 transition"
                                    style={{ background: PRIMARY }}>
                                    <Phone size={10}/> {m.phone}
                                  </a>
                                )}
                                {m.email && (
                                  <a href={`mailto:${m.email}`}
                                    data-testid={`email-${m.name.replace(/\s+/g, '-').toLowerCase()}`}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-white shadow hover:scale-105 transition"
                                    style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                                    <Mail size={10}/> Email
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {page.kind === "rich" && (
            <div data-testid="rich-content" className="space-y-10">
              {(page.data.sections || []).map((s, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}>
                  <h2 className="font-black uppercase italic mb-4" style={{ color: PRIMARY, fontSize: "clamp(1.25rem, 2.5vw, 1.85rem)" }}>
                    {s.title}
                  </h2>
                  {s.paragraphs?.map((p, j) => (
                    <p key={j} className="text-slate-700 leading-relaxed text-[16px] mb-3">{p}</p>
                  ))}
                  {s.bullets && (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                      {s.bullets.map((b) => (
                        <div key={b.title} className="rounded-2xl bg-white shadow-md border border-slate-100 p-5 hover:border-pink-300 hover:shadow-xl transition">
                          <div className="flex items-start gap-3">
                            <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white"
                              style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                              <CheckCircle2 size={16}/>
                            </div>
                            <div>
                              <h3 className="font-black uppercase text-sm mb-1" style={{ color: PRIMARY }}>{b.title}</h3>
                              <p className="text-slate-600 text-sm leading-snug">{b.text}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
              {(page.data.cta || page.data.contact_email) && (
                <div className="text-center pt-4 flex flex-wrap items-center justify-center gap-3">
                  {page.data.cta && (
                    <Link to={page.data.cta.to}
                      className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-black uppercase text-xs tracking-widest text-white shadow-lg hover:scale-105 transition"
                      style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                      {page.data.cta.label} <ArrowRight size={14}/>
                    </Link>
                  )}
                  {page.data.contact_email && (
                    <a href={`mailto:${page.data.contact_email}`}
                      className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-black uppercase text-xs tracking-widest border-2 hover:bg-pink-50 transition"
                      style={{ borderColor: PRIMARY, color: PRIMARY }}>
                      <Mail size={14}/> {page.data.contact_email}
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          {page.kind === "highlights" && (
            <div className="grid sm:grid-cols-3 gap-5">
              {page.data.highlights.map((h, i) => (
                <motion.div key={h.label} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="rounded-3xl text-center text-white p-8 shadow-2xl" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${SECONDARY})` }}>
                  <div className="font-black text-5xl">{h.value}</div>
                  <div className="text-pink-200 font-black uppercase tracking-widest text-[10px] mt-1">{h.label}</div>
                </motion.div>
              ))}
            </div>
          )}

          {page.kind === "simple" && (
            <div className="text-center">
              <Link to="/sport/rejoindre"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-black uppercase text-xs tracking-widest text-white shadow-lg hover:scale-105 transition"
                style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                Rejoindre le club <ArrowRight size={14}/>
              </Link>
            </div>
          )}

          {page.kind === "roster" && (
            <div className="space-y-12">
              {/* Players grid (group 1 — Centre de formation if group_label set, otherwise main effectif) */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <h2 className="font-black uppercase italic" style={{ color: PRIMARY, fontSize: "clamp(1.4rem, 2.6vw, 2rem)" }}>
                    {page.data.group_label || "L'effectif"}
                  </h2>
                  <span className="text-slate-400 text-xs font-bold tabular-nums">{page.data.players.length} joueurs</span>
                  <div className="flex-1 h-px bg-gradient-to-r from-pink-500/40 to-transparent"/>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
                  {page.data.players.map((p, i) => {
                    const fallback = page.gender === "female" ? FEMALE_SILHOUETTE : MALE_SILHOUETTE;
                    return (
                    <motion.div
                      key={`${p.number}-${p.name}`}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: Math.min(0.4, i * 0.04) }}
                      className="group relative rounded-2xl overflow-hidden bg-white shadow-md hover:shadow-2xl border border-slate-100 hover:border-pink-300 transition"
                      data-testid={`roster-player-${p.number}`}
                    >
                      <div className="relative aspect-[3/4] overflow-hidden" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` }}>
                        <img
                          src={p.photo || fallback}
                          alt={p.name}
                          loading="lazy"
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          onError={(e) => { e.currentTarget.src = fallback; }}
                        />
                        <span className="absolute top-2 left-2 font-blackhawk italic text-white text-3xl sm:text-4xl leading-none" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.7)" }}>
                          #{p.number}
                        </span>
                        <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4" style={{ background: "linear-gradient(180deg, transparent 0%, rgba(22,7,50,0.92) 100%)" }}>
                          <div className="text-white font-black uppercase text-sm leading-tight" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>{p.name}</div>
                          <div className="text-pink-300 text-[10px] font-black uppercase tracking-widest mt-1">{p.position}</div>
                        </div>
                      </div>
                    </motion.div>
                  );})}
                </div>
              </div>

              {/* Optional reserve_players group */}
              {page.data.reserve_players?.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <h2 className="font-black uppercase italic" style={{ color: PRIMARY, fontSize: "clamp(1.4rem, 2.6vw, 2rem)" }}>
                      Équipe réserve
                    </h2>
                    <span className="text-slate-400 text-xs font-bold tabular-nums">{page.data.reserve_players.length} joueurs</span>
                    <div className="flex-1 h-px bg-gradient-to-r from-pink-500/40 to-transparent"/>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {page.data.reserve_players.map((p) => {
                      const fallback = page.gender === "female" ? FEMALE_SILHOUETTE : MALE_SILHOUETTE;
                      return (
                      <div key={`${p.number}-${p.name}`} className="rounded-2xl overflow-hidden bg-white shadow border border-slate-100 hover:border-pink-300 transition group" data-testid={`reserve-player-${p.number}`}>
                        <div className="relative aspect-[3/4] overflow-hidden" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` }}>
                          <img src={p.photo || fallback} alt={p.name} loading="lazy" className="w-full h-full object-cover object-top opacity-90"
                               onError={(e) => { e.currentTarget.src = fallback; }}/>
                          <span className="absolute top-2 left-2 font-blackhawk italic text-white text-2xl sm:text-3xl leading-none" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.7)" }}>#{p.number}</span>
                          <div className="absolute inset-x-0 bottom-0 p-2.5" style={{ background: "linear-gradient(180deg, transparent 0%, rgba(22,7,50,0.92) 100%)" }}>
                            <div className="text-white font-black uppercase text-xs leading-tight">{p.name}</div>
                            <div className="text-pink-300 text-[9px] font-black uppercase tracking-widest mt-0.5">{p.position}</div>
                          </div>
                        </div>
                      </div>
                    );})}
                  </div>
                </div>
              )}

              {/* Staff — face well-framed via object-top */}
              {page.data.staff?.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <h2 className="font-black uppercase italic" style={{ color: PRIMARY, fontSize: "clamp(1.4rem, 2.6vw, 2rem)" }}>Le staff</h2>
                    <div className="flex-1 h-px bg-gradient-to-r from-pink-500/40 to-transparent"/>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {page.data.staff.map((s) => {
                      const fallback = page.gender === "female" ? FEMALE_SILHOUETTE : MALE_SILHOUETTE;
                      return (
                      <div key={s.name} className="rounded-2xl bg-white shadow-md border border-slate-100 p-4 flex items-center gap-4 hover:border-pink-300 transition" data-testid={`roster-staff-${s.name.replace(/\s+/g, '-').toLowerCase()}`}>
                        <div className="w-16 h-16 rounded-full overflow-hidden ring-4 ring-white shadow-lg shrink-0 bg-slate-100">
                          <img src={s.photo || fallback} alt={s.name} className="w-full h-full object-cover object-[center_top]" loading="lazy"
                               onError={(e) => { e.currentTarget.src = fallback; }}/>
                        </div>
                        <div className="min-w-0">
                          <div className="font-black uppercase text-sm" style={{ color: PRIMARY }}>{s.name}</div>
                          <div className="text-slate-500 text-xs mt-0.5">{s.role}</div>
                        </div>
                      </div>
                    );})}
                  </div>
                </div>
              )}

              {/* Competition (classement + calendrier links) */}
              {page.competition && (
                <div id="classement" className="rounded-2xl overflow-hidden shadow-xl" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` }} data-testid="roster-competition">
                  <div className="p-5 sm:p-6 text-white">
                    <div className="flex items-center gap-2 text-pink-300 font-black tracking-[0.3em] text-[10px] uppercase mb-2">
                      <Trophy size={11}/> Compétition
                    </div>
                    <div className="font-blackhawk italic uppercase text-2xl sm:text-3xl leading-tight">{page.competition.label}</div>
                    <p className="text-white/70 text-sm mt-3 max-w-xl">Classement et calendrier officiels mis à jour en temps réel par la Fédération Française de Handball.</p>
                    <div className="mt-5 flex flex-wrap gap-3">
                      <a href={page.competition.standings_url} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-black uppercase text-[11px] tracking-widest text-white shadow-lg hover:scale-105 transition"
                         style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
                         data-testid="roster-classement-link">
                        <Trophy size={13}/> Voir le classement <ArrowRight size={12}/>
                      </a>
                      {page.competition.calendar_url && (
                        <a href={page.competition.calendar_url} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-black uppercase text-[11px] tracking-widest border-2 border-white/30 text-white hover:bg-white/15 transition"
                           data-testid="roster-calendar-link">
                          📅 Voir le calendrier <ArrowRight size={12}/>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {page.data.cta_email && (
                <div className="text-center pt-4">
                  <a href={`mailto:${page.data.cta_email}`} className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-black uppercase text-xs tracking-widest text-white shadow-lg hover:scale-105 transition" style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                    <Mail size={14}/> Nous contacter <ArrowRight size={14}/>
                  </a>
                </div>
              )}
            </div>
          )}

          {/* ALUMNI — formation graduates */}
          {page.kind === "alumni" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {page.data.alumni.map((a, i) => (
                <motion.div key={a.name} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(0.4, i * 0.05) }}
                  className="rounded-2xl bg-white shadow-md hover:shadow-2xl border border-slate-100 hover:border-pink-300 transition overflow-hidden flex flex-col" data-testid={`alumni-${a.name.replace(/\s+/g, '-').toLowerCase()}`}>
                  <div className="aspect-[16/9] bg-slate-200 overflow-hidden">
                    <img src={a.photo} alt={a.name} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"/>
                  </div>
                  <div className="p-5 flex-1">
                    <h3 className="font-black uppercase italic text-lg leading-tight" style={{ color: PRIMARY }}>{a.name}</h3>
                    <p className="text-slate-600 text-sm mt-2 leading-relaxed">{a.story}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* TEAMS GRID — all club teams + venues */}
          {page.kind === "teams-grid" && (
            <div className="space-y-10">
              {/* Stats */}
              {page.data.stats?.length > 0 && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {page.data.stats.map((s) => (
                    <div key={s.label} className="rounded-2xl text-center text-white p-5 shadow-2xl" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${SECONDARY})` }}>
                      <div className="font-black text-3xl sm:text-4xl">{s.value}</div>
                      <div className="text-pink-200 font-black uppercase tracking-widest text-[10px] mt-1">{s.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {page.data.rentree_info && (
                <div className="rounded-2xl bg-pink-50 border border-pink-200 p-5">
                  <h3 className="font-black uppercase italic text-lg mb-2" style={{ color: SECONDARY }}>{page.data.rentree_info.title}</h3>
                  <ul className="space-y-1.5 text-sm text-slate-700">
                    {page.data.rentree_info.items.map((it) => <li key={it} className="flex gap-2"><span className="text-pink-500">•</span><span>{it}</span></li>)}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {page.data.groups.map((g, i) => (
                  <motion.div key={g.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(0.4, i * 0.04) }}
                    className="rounded-2xl bg-white shadow-md hover:shadow-xl border border-slate-100 hover:border-pink-300 transition p-5">
                    <div className="flex items-start gap-3 mb-3">
                      <span className="text-3xl">{g.icon}</span>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-black uppercase italic leading-tight" style={{ color: PRIMARY, fontSize: "1.05rem" }}>{g.title}</h3>
                        <div className="text-pink-600 text-[10px] font-black uppercase tracking-widest mt-1">{g.team_label}</div>
                      </div>
                    </div>
                    <ul className="space-y-1.5 text-sm text-slate-600">
                      {g.items.map((it) => <li key={it} className="flex gap-2"><span className="text-pink-400 shrink-0">▸</span><span>{it}</span></li>)}
                    </ul>
                  </motion.div>
                ))}
              </div>

              {/* Venues */}
              {page.data.venues?.length > 0 && (
                <div>
                  <h3 className="font-black uppercase italic text-2xl mb-5" style={{ color: PRIMARY }}>Les salles d'entraînement</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {page.data.venues.map((v) => (
                      <div key={v.name} className="rounded-2xl bg-white shadow border border-slate-100 p-4 flex gap-3">
                        <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow shrink-0" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${SECONDARY})` }}>📍</span>
                        <div className="min-w-0">
                          <div className="font-black uppercase text-sm" style={{ color: PRIMARY }}>{v.name}</div>
                          <div className="text-slate-500 text-xs mt-0.5">{v.address}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* JOIN — Inscriptions saison */}
          {page.kind === "join" && (
            <div className="space-y-10">
              <div>
                <h2 className="font-black uppercase italic text-2xl mb-5" style={{ color: PRIMARY }}>Catégories de pratique</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {page.data.categories.map((c) => (
                    <div key={c.label} className="rounded-2xl bg-white shadow border border-slate-100 hover:border-pink-300 transition p-4 text-center">
                      <div className="text-3xl mb-2">{c.icon}</div>
                      <div className="font-black uppercase text-sm" style={{ color: PRIMARY }}>{c.label}</div>
                      <div className="text-slate-500 text-[11px] font-bold mt-1">{c.years}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="rounded-2xl bg-white shadow-md border border-slate-100 p-6">
                  <h3 className="font-black uppercase italic text-xl mb-4" style={{ color: PRIMARY }}>Documents à fournir</h3>
                  <ul className="space-y-2.5">
                    {page.data.documents.map((d) => (
                      <li key={d.label}>
                        <a href={d.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-slate-50 hover:bg-pink-50 transition group">
                          <span className="w-8 h-8 rounded-lg bg-pink-100 group-hover:bg-pink-200 text-pink-700 flex items-center justify-center text-xs font-black shrink-0">PDF</span>
                          <span className="text-sm text-slate-700 group-hover:text-pink-700 font-bold flex-1">{d.label}</span>
                          <ArrowRight size={14} className="text-pink-500 shrink-0"/>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-2xl bg-white shadow-md border border-slate-100 p-6">
                  <h3 className="font-black uppercase italic text-xl mb-4" style={{ color: PRIMARY }}>Comment s'inscrire ?</h3>
                  <ol className="space-y-3 mb-5">
                    {page.data.steps.map((s, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="w-7 h-7 rounded-full text-white font-black text-xs flex items-center justify-center shrink-0" style={{ background: SECONDARY }}>{i + 1}</span>
                        <span className="text-sm text-slate-700">{s}</span>
                      </li>
                    ))}
                  </ol>
                  <a href={page.data.helloasso_url} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-black uppercase text-xs tracking-widest text-white shadow-lg hover:scale-105 transition" style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }} data-testid="join-helloasso-btn">
                    S'inscrire sur HelloAsso <ArrowRight size={14}/>
                  </a>
                </div>
              </div>

              {page.data.contact_email && (
                <div className="text-center">
                  <p className="text-slate-600 text-sm mb-3">Un doute ? Une question ? Notre équipe vous accompagne.</p>
                  <a href={`mailto:${page.data.contact_email}`} className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-black uppercase text-xs tracking-widest border-2 hover:bg-pink-50 transition" style={{ borderColor: PRIMARY, color: PRIMARY }}>
                    <Mail size={14}/> {page.data.contact_email}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* PRESS KIT — accreditations + media kit downloads */}
          {page.kind === "press-kit" && (
            <div className="space-y-10">
              <div className="rounded-2xl bg-white shadow-md border border-slate-100 p-6">
                <h2 className="font-black uppercase italic text-xl mb-3" style={{ color: PRIMARY }}>Règles d'accréditation</h2>
                <ul className="space-y-2 text-sm text-slate-700">
                  {page.data.rules.map((r) => <li key={r} className="flex gap-2"><span className="text-pink-500 shrink-0">▸</span><span>{r}</span></li>)}
                </ul>
              </div>

              {page.data.contact && (
                <div className="rounded-2xl shadow-xl p-6 text-white" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` }}>
                  <h3 className="font-black uppercase italic text-xl mb-3">Votre contact presse</h3>
                  <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex-1 min-w-[240px]">
                      <div className="font-black text-lg">{page.data.contact.name}</div>
                      <div className="text-pink-200 text-sm">{page.data.contact.role}</div>
                    </div>
                    <a href={`mailto:${page.data.contact.email}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-black uppercase text-xs tracking-widest bg-white/20 hover:bg-white/30 transition backdrop-blur">
                      <Mail size={13}/> {page.data.contact.email}
                    </a>
                    <a href={`tel:${page.data.contact.phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-black uppercase text-xs tracking-widest bg-white/20 hover:bg-white/30 transition backdrop-blur">
                      📞 {page.data.contact.phone}
                    </a>
                  </div>
                </div>
              )}

              <div>
                <h2 className="font-black uppercase italic text-2xl mb-5" style={{ color: PRIMARY }}>Kit médias téléchargeable</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {page.data.media_kit.map((k) => (
                    <a key={k.label} href={k.url} target="_blank" rel="noopener noreferrer" className="group rounded-2xl bg-white shadow border border-slate-100 hover:border-pink-300 hover:shadow-xl transition p-5 flex items-center gap-4" data-testid={`mediakit-${k.label.replace(/\s+/g, '-').toLowerCase().slice(0, 30)}`}>
                      <span className="w-12 h-12 rounded-xl flex items-center justify-center text-white shadow text-lg shrink-0" style={{ background: `linear-gradient(135deg, ${SECONDARY}, ${PRIMARY})` }}>{k.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-black text-sm leading-tight" style={{ color: PRIMARY }}>{k.label}</div>
                        <div className="text-pink-600 text-[11px] font-black uppercase tracking-widest mt-1 inline-flex items-center gap-1">Télécharger <ArrowRight size={11} className="group-hover:translate-x-0.5 transition"/></div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
